import * as React from 'react';
import cx from 'classnames';
import ReactSelect, { SelectInstance } from 'react-select';

import { FocusEventHandler } from 'types/typescript/eventHandlers';

import './styles/Select.scss';

export type Option = {
  label: string;
  value?: string | number;
};

/**
 * ## A styled [Select](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/select) input built using [React Select](https://github.com/JedWatson/react-select).
 * ### Use a select when you want the user to make exactly one option out of a list of options. Particularly useful for lists with a lot of options (6 or more) or lists with an unknown or variable number of options.
 *
 * ```example
 * <Field>
 *   <Label hidden>Label</Label>
 *   <Select
 *     options={[
 *       {
 *         label: 'Option #1',
 *         value: 'option-1',
 *       },
 *       {
 *         label: 'Option #2',
 *         value: 'option-2',
 *       },
 *       {
 *         label: 'Option #3',
 *         value: 'option-3',
 *       },
 *     ]}
 *   />
 * </Field>
 * ```
 *
 * ```example
 * <Field>
 *   <Label hidden>Label</Label>
 *   <Select
 *     clearable
 *     options={[
 *       { label: 'Option #1', value: 'option-1' },
 *       { label: 'Option #2', value: 'option-2' },
 *       { label: 'Option #3', value: 'option-3' },
 *     ]}
 *     placeholder="Select placeholder"
 *     searchable
 *   />
 * </Field>
 * ```
 *
 *
 * # Events
 *
 * ```example
 * <Field>
 *   <Label hidden>Label</Label>
 *   <Select
 *     options={[
 *       { label: 'Option #1', value: 'option-1' },
 *       { label: 'Option #2', value: 'option-2' },
 *       { label: 'Option #3', value: 'option-3' },
 *     ]}
 *     onChange={() => console.log('onChange')}
 *     onInputKeyUp={() => console.log('onInputKeyUp')}
 *   />
 * </Field>
 * ```
 */

export interface Props {
  isMulti?: boolean;
  autoFocus?: boolean;
  className?: string;
  clearable?: boolean;
  name?: string;
  options: Option[];
  placeholder?: string;
  value?: any;
  searchable?: boolean;
  hideSelectedOptions?: boolean;
  onChange?: Function;
  onInputKeyUp?: Function;
  onFocus?: FocusEventHandler;
  onMenuOpen?: () => void;
  disabled?: boolean;
  inline?: boolean;
  full?: boolean;
  appendedLinkItem?: {
    label: string;
    onClick: () => void;
  };
  components?: any;
  closeMenuOnSelect?: boolean;
  isDisabled?: boolean;
  styles?: any;
}

type DefaultProps = {
  clearable: boolean;
  options: Option[];
};

class Select extends React.Component<Props> {
  static defaultProps: DefaultProps = {
    clearable: false,
    options: [],
  };

  _select: SelectInstance | null = null;

  handleInputKeyUp = () => {
    const { onInputKeyUp, options } = this.props;

    if (!this._select) {
      return;
    }

    const inputText = this._select.state.selectValue;

    const selected = options.find((option) => {
      let optionLabel;
      let inputLabel;

      if (option.label) {
        optionLabel = String(option.label).toLowerCase();
      }

      if (inputText) {
        inputLabel = String(inputText).toLowerCase();
      }

      return inputLabel === optionLabel;
    });

    if (onInputKeyUp) {
      onInputKeyUp(selected);
    }
  };

  handleChange = (option: any) => {
    const { onChange } = this.props;
    if (this.props.appendedLinkItem && option.value === 'appended-link-item') {
      this.props.appendedLinkItem.onClick();
    } else if (onChange) {
      if (this.props.isMulti) {
        onChange(
          option?.map((o: any) => {
            return o?.value;
          })
        );
      } else {
        onChange(option?.value);
      }
    }
  };

  render() {
    const {
      autoFocus,
      isMulti,
      className,
      clearable,
      name,
      options,
      placeholder,
      searchable,
      hideSelectedOptions,
      value,
      onFocus,
      disabled,
      onMenuOpen,
      inline,
      full,
      appendedLinkItem,
      components,
      onChange,
      styles,
      ...opts
    } = this.props;

    for (const option of options) {
      option.value = String(option.value);
    }

    let appendedLinkItemOption;

    if (appendedLinkItem) {
      appendedLinkItemOption = {
        label: appendedLinkItem.label,
        value: 'appended-link-item',
      };
    }

    const valueObject = options.find((option) => option?.value == value);

    return (
      <ReactSelect
        isMulti={isMulti}
        autoFocus={autoFocus}
        isClearable={clearable}
        name={name}
        options={appendedLinkItemOption ? [...options, appendedLinkItemOption] : options}
        placeholder={placeholder}
        hideSelectedOptions={hideSelectedOptions}
        isSearchable={searchable}
        value={(isMulti ? value : valueObject) ?? ''}
        className={cx(
          className,
          'Select',
          inline && 'Select--inline',
          full && 'Select--full',
          appendedLinkItem && 'Select--with-appended-link-item'
        )}
        classNamePrefix="Select"
        onChange={this.handleChange}
        onFocus={onFocus}
        isDisabled={disabled}
        onMenuOpen={onMenuOpen}
        components={components}
        styles={styles}
        {...opts}
      />
    );
  }
}

export default Select;
